import { buildQuery } from '@pn/core/domain/query';
import { UserPlans } from '@pn/core/domain/user';
import {
  createInitInternalState,
  getLayerId,
  ItemsFolder,
  type ApiDataSource,
  type GeoJsonDataSource,
  type IWorkspaceItemMapper,
  type NoDataSource,
  type WorkspaceItem,
} from '@pn/core/domain/workspace';
import { GLOBAL_ADMIN_USER_IDS } from '@pn/core/permissions/editability';
import {
  getCurrentUser,
  getCurrentUserId,
} from '@pn/core/storage/user/currentUserAccessors';
import { setMapping } from '@pn/core/storage/workspace/mappings';
import { assert } from '@pn/core/utils/assert';
import { nullToUndefined } from '@pn/core/utils/logic';
import { apiProjectUserMapper } from '@pn/services/api/user/apiUserMapper';
import { mapboxLayerMapper } from '@pn/services/map/mapbox/mappers/mapboxLayerMapper';
import { generateEmptyGeoJsonFeatureCollection } from '@pn/services/utils/geojson';
import { isNil, isString } from 'lodash-es';
import { apiParquetMappingMapper } from './apiParquetMappingMapper';
import type {
  ApiLayerItem,
  ApiLayerItemPayload,
  ExtendedMapboxLayer,
} from './types';

export const apiLayerItemMapper: IWorkspaceItemMapper<
  ApiLayerItem,
  ApiLayerItemPayload
> = {
  toWorkspaceItem: (apiLayerItem) => {
    assert(
      apiLayerItem.layers_definition,
      `layers_definition is missing in ${apiLayerItem.id}`
    );

    const user = getCurrentUser();

    const mapping = !isNil(apiLayerItem.mapping)
      ? apiParquetMappingMapper.toDomainMapping(apiLayerItem.mapping)
      : undefined;

    if (!isNil(mapping)) setMapping(apiLayerItem.id, mapping);

    return {
      folder: getFolder(apiLayerItem),
      isTemporary:
        apiLayerItem.origin === 'stackdx' &&
        user?.userPlan !== UserPlans.EnterpriseOwner, // hide Stack source layers from the Library (unless Enterprise Owner)
      id: apiLayerItem.id,
      dataType: apiLayerItem.id,
      name: apiLayerItem.name,
      numberOfElements: nullToUndefined(apiLayerItem.items_length),
      colorIndicator: nullToUndefined(apiLayerItem.color_indicator),
      itemType: 'layer',
      origin: apiLayerItem.origin,
      createdAt: apiLayerItem.created_at,
      updatedAt: apiLayerItem.updated_at,
      createdBy: !isNil(apiLayerItem.created_by)
        ? apiProjectUserMapper.toDomainProjectUser(apiLayerItem.created_by)
        : undefined,
      isShared: apiLayerItem.shared,
      isGlobal: apiLayerItem.global,
      map: {
        layers: (
          apiLayerItem.layers_definition_override ??
          apiLayerItem.layers_definition
        ).map((mapboxLayer, index) =>
          mapMapboxLayerToDomainLayer(apiLayerItem, mapboxLayer, index)
        ),
        originalLayers:
          apiLayerItem.created_by?.id === user?.id
            ? undefined
            : apiLayerItem.layers_definition.map((mapboxLayer, index) =>
                mapMapboxLayerToDomainLayer(apiLayerItem, mapboxLayer, index)
              ),
      },
      dataSource: mapToDomainDataSource(apiLayerItem),
      detailsSource: apiLayerItem.has_api_details ? 'api' : 'local',
      query: buildQuery({
        id: apiLayerItem.id,
        dataType: apiLayerItem.id,
      }),
      ...createInitInternalState({ isVisualized: false }),
    };
  },
  toOriginalItem: (item) => {
    return {
      name: item.name,
      layers_definition: item.map.layers.map(mapboxLayerMapper.toTargetLayer),
    };
  },
};

function getFolder(apiLayerItem: ApiLayerItem): WorkspaceItem['folder'] {
  const userId = getCurrentUserId();

  if (apiLayerItem.global && !GLOBAL_ADMIN_USER_IDS.includes(userId!)) {
    // Note: global user's layers will go to Personal folder for convenience
    return undefined; // do not show in the Library outside of projects
  } else if (apiLayerItem.origin === 'stackdx') {
    return ItemsFolder.Stack;
  } else if (apiLayerItem.origin === 'arcgis') {
    return ItemsFolder.ArcGIS;
  } else if (!isNil(userId) && apiLayerItem.created_by?.id === userId) {
    return ItemsFolder.Personal;
  } else {
    return apiLayerItem.shared ? ItemsFolder.Shared : undefined;
  }
}

function mapToDomainDataSource(
  apiLayerItem: ApiLayerItem
): ApiDataSource | GeoJsonDataSource | NoDataSource {
  const { id, data_source_type, data_source_url, data_source_map_fields } =
    apiLayerItem;

  switch (data_source_type) {
    case 'postgres':
    case 'parquet':
      assert(
        isString(data_source_url),
        `Failed to map ${id}: data_source_url must be a string`
      );
      return {
        type: 'api',
        source: data_source_type,
        url: data_source_url,
        requiredMapDataFields: data_source_map_fields,
        requiredTableDataFields: ['internal_id', 'geometry'],
      };
    case 'geojson':
      assert(
        isString(data_source_url),
        `Failed to map ${id}: data_source_url must be a string`
      );
      return {
        type: 'geojson',
        url: data_source_url,
      };
    case 'none':
      return { type: 'none' };
  }
}

function mapMapboxLayerToDomainLayer(
  apiLayerItem: ApiLayerItem,
  mapboxLayer: ExtendedMapboxLayer,
  index: number
) {
  return {
    name: mapboxLayer.name,
    ...mapboxLayerMapper.toDomainLayer(
      {
        source: {
          type: 'geojson',
          data: generateEmptyGeoJsonFeatureCollection(),
        },
        'source-layer': undefined,
        ...mapboxLayer, // will override the source if one is present
        id: getLayerId(apiLayerItem.id, apiLayerItem.id, index),
      },
      mapboxLayer.render_as_points ?? false
    ),
  };
}
